import { useEffect, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import type {
  BookableResourceType,
  FilePurposeType,
  ResourceAction,
  VisibilityStatus,
} from "../types";
import { formatLabel } from "../labels";

const RESOURCE_TYPES: BookableResourceType[] = [
  "court",
  "field",
  "studio",
  "pool_lane",
  "meeting_room",
  "rental_equipment",
  "future_governed_type",
];

const VISIBILITY: VisibilityStatus[] = ["visible", "hidden", "paused", "restricted"];

const RESOURCE_ACTIONS: ResourceAction[] = [
  "make_available",
  "make_unavailable",
  "pause",
  "start_maintenance",
  "end_maintenance",
  "restrict",
  "retire",
  "restore",
];

const FILE_PURPOSES: FilePurposeType[] = [
  "business_document",
  "business_media",
  "audit_evidence",
  "policy_attachment",
  "future_governed_type",
];

export function ReasonActionDialog({
  open,
  title,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => void onSubmit(reason)}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function CreateResourceDialog({
  open,
  defaultCapabilityId,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  defaultCapabilityId?: string;
  onClose: () => void;
  onSubmit: (input: {
    business_capability_id: string;
    name: string;
    resource_type: BookableResourceType;
    capacity?: number | null;
    visibility_status?: VisibilityStatus;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [capabilityId, setCapabilityId] = useState(defaultCapabilityId ?? "");
  const [name, setName] = useState("");
  const [resourceType, setResourceType] = useState<BookableResourceType>("court");
  const [capacity, setCapacity] = useState("");
  const [visibility, setVisibility] = useState<VisibilityStatus>("visible");

  useEffect(() => {
    if (open) setCapabilityId(defaultCapabilityId ?? "");
  }, [open, defaultCapabilityId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Bookable Resource"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={capabilityId.trim().length === 0 || name.trim().length === 0}
            onClick={() => {
              const parsed = capacity.trim() === "" ? undefined : Number(capacity);
              void onSubmit({
                business_capability_id: capabilityId.trim(),
                name: name.trim(),
                resource_type: resourceType,
                ...(parsed !== undefined && Number.isFinite(parsed)
                  ? { capacity: parsed }
                  : {}),
                visibility_status: visibility,
              });
            }}
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          A Bookable Resource belongs to one Business Capability. The API copies
          Business and Market from that capability.
        </Text>
        <Input
          label="Business Capability ID"
          value={capabilityId}
          onChange={(event) => setCapabilityId(event.target.value)}
        />
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Select
          label="Resource type"
          value={resourceType}
          onChange={(event) =>
            setResourceType(event.target.value as BookableResourceType)
          }
          options={RESOURCE_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Capacity (optional)"
          type="number"
          min={1}
          value={capacity}
          onChange={(event) => setCapacity(event.target.value)}
        />
        <Select
          label="Visibility"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityStatus)
          }
          options={VISIBILITY.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function EditResourceDialog({
  open,
  name,
  capacity,
  visibilityStatus,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  name: string;
  capacity: number | null;
  visibilityStatus: VisibilityStatus;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    capacity: number | null;
    visibility_status: VisibilityStatus;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [displayName, setDisplayName] = useState(name);
  const [capacityValue, setCapacityValue] = useState(
    capacity === null ? "" : String(capacity),
  );
  const [visibility, setVisibility] = useState(visibilityStatus);

  useEffect(() => {
    if (!open) return;
    setDisplayName(name);
    setCapacityValue(capacity === null ? "" : String(capacity));
    setVisibility(visibilityStatus);
  }, [open, name, capacity, visibilityStatus]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Bookable Resource"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={displayName.trim().length === 0}
            onClick={() =>
              void onSubmit({
                name: displayName.trim(),
                capacity:
                  capacityValue.trim() === ""
                    ? null
                    : Number(capacityValue),
                visibility_status: visibility,
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="Name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <Input
          label="Capacity"
          type="number"
          min={1}
          value={capacityValue}
          onChange={(event) => setCapacityValue(event.target.value)}
          hint="Leave blank to clear capacity."
        />
        <Select
          label="Visibility"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityStatus)
          }
          options={VISIBILITY.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Update failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function ResourceActionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { action: ResourceAction; reason: string }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState<ResourceAction>("make_available");
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Resource status action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "retire" || action === "restrict" ? "danger" : "primary"
            }
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => void onSubmit({ action, reason })}
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Status changes stay on this resource. The backend remains
          authoritative for allowed transitions.
        </Text>
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value as ResourceAction)}
          options={RESOURCE_ACTIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function AttachFileDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    file_asset_id: string;
    purpose_type: FilePurposeType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [fileAssetId, setFileAssetId] = useState("");
  const [purposeType, setPurposeType] =
    useState<FilePurposeType>("business_media");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Attach File Asset"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={fileAssetId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                file_asset_id: fileAssetId.trim(),
                purpose_type: purposeType,
              })
            }
          >
            Attach
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="File Asset ID"
          value={fileAssetId}
          onChange={(event) => setFileAssetId(event.target.value)}
        />
        <Select
          label="Purpose"
          value={purposeType}
          onChange={(event) =>
            setPurposeType(event.target.value as FilePurposeType)
          }
          options={FILE_PURPOSES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Attach failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
